import { I_ControlNets } from '@shared/typings/build/request/app';
import { AppBuildStore } from '@shared/stores/AppBuild';
import { IBuildFormNode } from '@shared/typings/build/classes/node';
import { TaskState, TaskType } from '@shared/typings/task';
import { AppCategory, AppCategoryType, StyleType } from '@shared/typings/models/app';
import { Application } from '@shared/typings/models/app/applicationList';
import { tryParseForm } from './parse';
import { tryParseJson } from '../json';
import { IBuildFlowParam } from '@shared/typings/flow/common/upload';
import { ENodeOutputTypeCode, INodeData } from '@shared/typings/flow/common/data';
/**
 * 过滤已选的和互斥的cnet
 * @param states
 * @param controlNets
 * @returns
 */
export const getFilterNetCardIDs = (
	states: IBuildFormNode.Node[],
	controlNets: I_ControlNets.RequestData['list'],
) => {
	let idList: string[] = [];
	const selectedNetIDs = states
		.filter((item) => item.type === 'cnet')
		.map((item) => item.cnet.id) as string[];
	controlNets.forEach((net) => {
		if (selectedNetIDs.includes(net.id)) {
			idList = idList.concat(net.disabledId || []);
		}
	});
	return idList;
};

/**
 * [图片引用功能: 上传时覆盖被引用表单项数据]
 */
export function uploadOverlayReferenceData(
	params: {
		// 上传图片的组件
		formItem: IBuildFormNode.CNet | IBuildFormNode.Digital;
		// 上传结果
		uploadRes: { path: string; imgName: string };
	},
	store: AppBuildStore,
) {
	const { formItem, uploadRes } = params;
	if (!formItem) return;
	const appBuildStore = store.getState();
	let refedItems: Array<IBuildFormNode.Node> = [];
	const formData = appBuildStore.consumeData;
	const ids = formItem.refedIds ?? [];
	refedItems = formData.filter((item) => item.type === 'cnet' && ids.includes(item.id));

	refedItems.forEach((item) => {
		appBuildStore.consumeDispatch({
			type: 'updateItem',
			payload: {
				id: item.id,
				imgData: uploadRes.imgName,
				path: uploadRes.path,
			},
		});
	});
}

/**
 * [连接起点可连接判断]
 */
function checkStartNodeConnectable(node: IBuildFormNode.CNet) {
	if (node.type === 'cnet') {
		return (
			(node.refedIds?.length ?? 0) === 0 &&
			(node.refIds?.length ?? 0) === 0 &&
			node.isLock === false
		);
	}
	return false;
}

/**
 * [连接终点可连接判断]
 */
function checkEndNodeConnectable(node: IBuildFormNode.CNet | IBuildFormNode.Digital) {
	if (node.type === 'cnet') {
		return (node.refIds?.length ?? 0) === 0 && node.isLock === false;
	} else if (node.type === 'digital') {
		return node.isLock === false;
	}
	return false;
}

/**
 * [图片引用功能: 链接表单项]
 */
export function linkNode(
	params: {
		startNode: IBuildFormNode.CNet;
		endNode: IBuildFormNode.CNet | IBuildFormNode.Digital;
	},
	store: AppBuildStore,
) {
	const { startNode, endNode } = params;
	if (!checkStartNodeConnectable(startNode)) return;
	if (!checkEndNodeConnectable(endNode)) return;

	const appBuildStore = store.getState();
	// 设置起点数据
	appBuildStore.buildDispatch({
		type: 'updateItem',
		payload: {
			id: startNode.id,
			refIds: [...(startNode.refIds ?? []), endNode.id],
		},
	});
	// 设置终点数据
	if (endNode.type === 'cnet') {
		appBuildStore.buildDispatch({
			type: 'updateItem',
			payload: {
				id: endNode.id,
				refedIds: [...(endNode.refedIds ?? []), startNode.id],
			},
		});
	} else if (endNode.type === 'digital') {
		appBuildStore.buildDispatch({
			type: 'updateItem',
			payload: {
				id: endNode.id,
				refedIds: [...(endNode.refedIds ?? []), startNode.id],
			},
		});
	}
	appBuildStore.changeState({
		matchLines: [
			...appBuildStore.matchLines,
			{
				startId: startNode.id,
				endId: endNode.id,
			},
		],
	});
}

/**
 * [图片引用功能: 取消表单项链接]
 */
export function unlinkNode(
	params: {
		startNode: IBuildFormNode.CNet;
		endNode: IBuildFormNode.CNet | IBuildFormNode.Digital;
	},
	store: AppBuildStore,
) {
	const { startNode, endNode } = params;
	if (startNode.type !== 'cnet' || !['cnet', 'digital'].includes(endNode.type)) return;
	const appBuildStore = store.getState();
	// 设置起点数据
	appBuildStore.buildDispatch({
		type: 'updateItem',
		payload: {
			id: startNode.id,
			refIds: (startNode.refIds ?? []).filter((id) => id !== endNode.id),
		},
	});
	// 设置终点数据
	if (endNode.type === 'cnet') {
		appBuildStore.buildDispatch({
			type: 'updateItem',
			payload: {
				id: endNode.id,
				refedIds: (endNode.refedIds ?? []).filter((id) => id !== startNode.id),
			},
		});
	} else if (endNode.type === 'digital') {
		appBuildStore.buildDispatch({
			type: 'updateItem',
			payload: {
				id: endNode.id,
				refedIds: ((endNode as IBuildFormNode.Digital).refedIds ?? []).filter(
					(id) => id !== startNode.id,
				),
			},
		});
	}
	appBuildStore.changeState({
		matchLines: appBuildStore.matchLines.filter((idsObj) => {
			return idsObj.startId !== startNode.id && idsObj.endId !== endNode.id;
		}),
	});
}

export enum TransformedTaskStatus {
	GENERATE_FINISH = 'generate_finish',
	GENERATE_FAILURE = 'generate_failure',
	GENERATE_GOING = 'generate_going',
	GENERATE_WAIT = 'generate_wait',
	OPTIMIZE_FINISH = 'optimize_finish',
	OPTIMIZE_FAILURE = 'optimize_failure',
	OPTIMIZE_GOING = 'optimize_going',
	OPTIMIZE_WAIT = 'optimize_wait',
	DELETED = 'deleted',
}

const GenerateStatus = {
	[TaskState.FINISH]: TransformedTaskStatus.GENERATE_FINISH,
	[TaskState.FAILURE]: TransformedTaskStatus.GENERATE_FAILURE,
	[TaskState.GOING]: TransformedTaskStatus.GENERATE_GOING,
	[TaskState.WAIT]: TransformedTaskStatus.GENERATE_WAIT,
	[TaskState.DELETED]: TransformedTaskStatus.DELETED,
} as const;

const OptimizeStatus = {
	[TaskState.FINISH]: TransformedTaskStatus.OPTIMIZE_FINISH,
	[TaskState.FAILURE]: TransformedTaskStatus.OPTIMIZE_FAILURE,
	[TaskState.GOING]: TransformedTaskStatus.OPTIMIZE_GOING,
	[TaskState.WAIT]: TransformedTaskStatus.OPTIMIZE_WAIT,
	[TaskState.DELETED]: TransformedTaskStatus.DELETED,
};

export const isGenerateTaskType = (type: TaskType) => {
	return type !== TaskType.OPTIMIZE;
};

export function transformTaskStatus(type: TaskType, state: TaskState) {
	return type == TaskType.OPTIMIZE ? OptimizeStatus[state] : GenerateStatus[state];
}

export function isVideoGen(appCategory?: AppCategoryType) {
	return appCategory === '1' || appCategory === '3' || appCategory === '4';
}

export function isFaceSwapFilter(appCategory?: AppCategoryType) {
	return appCategory === '2';
}

const FACE_SWAP_FILTER_COMPONENT_TYPES = ['faceSwap', 'video'] as const;

export function hasFaceSwapFilter(form?: string | null) {
	const nodes = tryParseForm(form ?? '');

	return nodes.some((node) =>
		FACE_SWAP_FILTER_COMPONENT_TYPES.includes(
			node.type as (typeof FACE_SWAP_FILTER_COMPONENT_TYPES)[number],
		),
	);
}

export function isLongVideoFilter(appCategory?: AppCategoryType) {
	return appCategory === '3' || appCategory === '4';
}

export function isVideoFilterType(appCategory?: AppCategoryType) {
	return appCategory === '1' || appCategory === '2' || appCategory === '3' || appCategory === '4';
}

export function isVideoAnimateApp(resource?: Application.Resource) {
	if (!resource || resource.type !== 'form') return false;
	return tryParseForm(resource.form).find((node: IBuildFormNode.Node) => {
		return node.type === 'animate';
	});
}

export function isLongVideoFlowApp(resource?: Application.Resource) {
	if (!resource || resource.type === 'form') return false;
	try {
		const node = tryParseJson(resource.node!) as IBuildFlowParam.Data;
		const { vertexes } = node;
		return vertexes.some((node) => node.type === ENodeOutputTypeCode.LongVideo);
	} catch {
		return false;
	}
}

// 判断当前 filter style 是否为 3.0 且 appCategory 为 0 （表示生图应用）
export function isStyle3Filter(resource?: Application.Resource, selectedStyleId?: string) {
	if (!resource || !selectedStyleId || resource.appCategory !== AppCategory.Image) return false;
	const currentStyle = resource.styleArr?.find((item) => item.id === selectedStyleId);
	return currentStyle?.sdVersion === StyleType['Style 3.0'];
}

// 判断最后一个节点是不是图片节点(节点式)
export function isImageNodeEnd(resource?: Application.Resource) {
	if (!resource || resource.type === 'form') return false;
	try {
		const node = tryParseJson(resource.node!) as IBuildFlowParam.Data;
		const { vertexes } = node;
		return vertexes.find((node) => node.type === ENodeOutputTypeCode.Image && node.isFinal);
	} catch {
		return false;
	}
}

// 判断节点式滤镜最后一个节点是否为视频输出且非动作模板
export function isVideoNodeEndWithoutAction(resource?: Application.Resource) {
	if (!resource || resource.type === 'form') return false;
	try {
		const node = tryParseJson(resource.node!) as IBuildFlowParam.Data;
		const { vertexes } = node;
		const videoNode = vertexes.find((v) => v.type === ENodeOutputTypeCode.Video && v.isFinal);
		if (!videoNode) return false;
		const selectId = (videoNode as INodeData.OutputVideo).videoInfo?.action?.selectId;
		return !selectId;
	} catch {
		return false;
	}
}

export function getVideoPlayStrategy(category?: AppCategoryType) {
	switch (category) {
		case '1':
			// 图生视频
			return { playbackRate: 1.5, maxDuration: Infinity };

		case '2':
			// 视频换脸
			return { playbackRate: 1, maxDuration: 2.5 };

		case '3':
			// 长视频
			return { playbackRate: 1.5, maxDuration: 5 };
		case '4':
			// 数字人（样式同长视频）
			return { playbackRate: 1.5, maxDuration: 5 };

		default:
			return { playbackRate: 1, maxDuration: Infinity };
	}
}
